import { useEffect, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { useTickets, useTicket, useCreateTicket, useReplyTicket, useCloseTicket, useMarkTicketRead, useTicketLimits } from "../hooks/useApi";
import { supportApi } from "../services/api";
import Card from "../components/Card";
import Button from "../components/Button";
import GlassModal from "../components/GlassModal";
import Badge from "../components/Badge";
import TicketMessageContent from "../components/TicketMessageContent";

const inputClass =
  "w-full h-10 px-3 rounded-btn border border-line text-small text-text-secondary bg-surface focus:outline-none focus:ring-2 focus:ring-primary/30 transition-all";

const allowedTypes = ["image/jpeg", "image/png", "application/pdf"];

const statusBadge: Record<string, { variant: "success" | "warning" | "error" | "info"; label: string }> = {
  open: { variant: "warning", label: "در انتظار پاسخ پشتیبانی" },
  answered: { variant: "success", label: "پاسخ داده شده" },
  closed: { variant: "info", label: "بسته شده" },
};

const priorityLabel: Record<string, string> = {
  low: "کم",
  medium: "متوسط",
  high: "زیاد",
};

const categoryLabel: Record<string, string> = {
  general: "عمومی",
  billing: "مالی و پرداخت",
  technical: "فنی",
  api: "API",
  account: "حساب کاربری",
};

interface Attachment {
  attachment_id: number;
  original_name: string;
  mime_type: string;
  size_bytes: number;
  scan_status: string;
}

function timeAgo(iso: string): string {
  const diff = Date.now() - new Date(iso).getTime();
  const minutes = Math.floor(diff / 60000);
  if (minutes < 1) return "هم‌اکنون";
  if (minutes < 60) return `${minutes.toLocaleString("fa-IR")} دقیقه پیش`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours.toLocaleString("fa-IR")} ساعت پیش`;
  const days = Math.floor(hours / 24);
  if (days < 30) return `${days.toLocaleString("fa-IR")} روز پیش`;
  return new Date(iso).toLocaleDateString("fa-IR");
}

function formatSize(bytes: number): string {
  if (!bytes) return "۰";
  if (bytes < 1024) return `${bytes} بایت`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toLocaleString("fa-IR", { maximumFractionDigits: 1 })} کیلوبایت`;
  return `${(bytes / (1024 * 1024)).toLocaleString("fa-IR", { maximumFractionDigits: 1 })} مگابایت`;
}

function validateFiles(files: File[], maxMb: number, maxCount: number): string | null {
  if (files.length > maxCount) {
    return `حداکثر ${maxCount} فایل در هر پیام مجاز است.`;
  }
  for (const file of files) {
    if (!allowedTypes.includes(file.type)) {
      return `فرمت «${file.name}» مجاز نیست. فقط تصاویر JPEG/PNG و فایل PDF مجاز است.`;
    }
    if (file.size > maxMb * 1024 * 1024) {
      return `حجم «${file.name}» بیش از سقف مجاز ${maxMb} مگابایت است.`;
    }
    if (file.size === 0) {
      return `فایل «${file.name}» خالی است.`;
    }
  }
  return null;
}

function FilePicker({
  files,
  onChange,
  error,
}: {
  files: File[];
  onChange: (next: File[]) => void;
  error: string | null;
}) {
  const ref = useRef<HTMLInputElement>(null);
  const remove = (index: number) => onChange(files.filter((_, i) => i !== index));
  return (
    <div>
      <input
        ref={ref}
        type="file"
        multiple
        accept="image/png,image/jpeg,application/pdf"
        className="hidden"
        onChange={(e) => {
          const picked = Array.from(e.target.files || []);
          onChange([...files, ...picked].slice(0, 3));
          e.target.value = "";
        }}
      />
      <div className="flex flex-wrap items-center gap-2">
        <button
          type="button"
          onClick={() => ref.current?.click()}
          className="inline-flex items-center gap-1.5 rounded-btn border border-dashed border-line px-3 py-2 text-small text-text-secondary hover:border-primary hover:text-primary transition-colors"
        >
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M21.44 11.05l-9.19 9.19a6 6 0 0 1-8.49-8.49l9.19-9.19a4 4 0 0 1 5.66 5.66l-9.2 9.19a2 2 0 0 1-2.83-2.83l8.49-8.48" />
          </svg>
          پیوست فایل
        </button>
        {files.length > 0 && (
          <button
            type="button"
            onClick={() => onChange([])}
            className="text-tiny text-text-tertiary hover:text-text-secondary transition-colors"
          >
            حذف همه
          </button>
        )}
      </div>
      {files.length > 0 && (
        <div className="flex flex-wrap gap-2 mt-2">
          {files.map((f, i) => (
            <span key={`${f.name}-${i}`} className="inline-flex items-center gap-1.5 rounded-lg bg-surface-hover border border-line px-2.5 py-1.5 text-tiny text-text-secondary">
              <span className="max-w-[160px] truncate">{f.name}</span>
              <button
                type="button"
                onClick={() => remove(i)}
                className="text-text-tertiary hover:text-red-500 transition-colors"
                aria-label="حذف فایل"
              >
                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
                  <line x1="18" y1="6" x2="6" y2="18" />
                  <line x1="6" y1="6" x2="18" y2="18" />
                </svg>
              </button>
            </span>
          ))}
        </div>
      )}
      {error && <p className="text-tiny text-red-500 mt-1.5">{error}</p>}
    </div>
  );
}

export default function Support() {
  const { data: tickets, isLoading } = useTickets();
  const { data: limits } = useTicketLimits();
  const [searchParams, setSearchParams] = useSearchParams();
  const paramTicket = searchParams.get("ticket");
  const [selectedId, setSelectedId] = useState<number | null>(
    paramTicket && !Number.isNaN(Number(paramTicket)) ? Number(paramTicket) : null
  );
  const { data: detail } = useTicket(selectedId);
  const create = useCreateTicket();
  const reply = useReplyTicket(selectedId);
  const close = useCloseTicket(selectedId);
  const markRead = useMarkTicketRead(selectedId);

  const maxOpen = limits?.max_open_tickets ?? 10;
  const openCount = limits?.open_count ?? (tickets as Record<string, unknown>[] | undefined)?.filter((t) => (t.status as string) !== "closed").length ?? 0;
  const canCreate = limits ? Boolean(limits.can_create) : openCount < maxOpen;
  const maxMb = limits?.upload_max_mb ?? 5;
  const maxFiles = limits?.upload_max_files ?? 3;

  const handleSelect = (ticketId: number) => {
    setSelectedId(ticketId);
    setSearchParams({ ticket: String(ticketId) }, { replace: true });
  };

  useEffect(() => {
    if (paramTicket && !Number.isNaN(Number(paramTicket))) {
      setSelectedId(Number(paramTicket));
    }
  }, [paramTicket]);

  useEffect(() => {
    if (selectedId !== null) {
      markRead.mutate();
    }
  }, [selectedId]);

  const [showCreate, setShowCreate] = useState(false);
  const [form, setForm] = useState({ subject: "", message: "", category: "general", priority: "medium" });
  const [createFiles, setCreateFiles] = useState<File[]>([]);
  const [createFileError, setCreateFileError] = useState<string | null>(null);
  const [replyText, setReplyText] = useState("");
  const [replyFiles, setReplyFiles] = useState<File[]>([]);
  const [replyFileError, setReplyFileError] = useState<string | null>(null);
  const [confirmClose, setConfirmClose] = useState(false);

  const handleCreate = () => {
    if (!form.subject.trim() || !form.message.trim()) return;
    const err = validateFiles(createFiles, maxMb, maxFiles);
    if (err) {
      setCreateFileError(err);
      return;
    }
    setCreateFileError(null);
    create.mutate(
      { ...form, files: createFiles },
      {
        onSuccess: () => {
          setShowCreate(false);
          setForm({ subject: "", message: "", category: "general", priority: "medium" });
          setCreateFiles([]);
        },
      }
    );
  };

  const handleReply = () => {
    if (!replyText.trim() || selectedId === null) return;
    const err = validateFiles(replyFiles, maxMb, maxFiles);
    if (err) {
      setReplyFileError(err);
      return;
    }
    setReplyFileError(null);
    reply.mutate(
      { message: replyText, files: replyFiles },
      { onSuccess: () => { setReplyText(""); setReplyFiles([]); } }
    );
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-h1 text-text-main">پشتیبانی</h1>
          <p className="text-body text-text-secondary mt-1">تماس با پشتیبانی هوش مصنوعی آشا و پیگیری تیکت‌ها</p>
          <p className="text-tiny text-text-tertiary mt-1">
            تیکت‌های باز: {openCount.toLocaleString("fa-IR")} / {maxOpen.toLocaleString("fa-IR")}
          </p>
        </div>
        <Button onClick={() => setShowCreate(true)} disabled={!canCreate} title={!canCreate ? `به سقف ${maxOpen} تیکت باز رسیده‌اید` : undefined}>
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
            <line x1="12" y1="5" x2="12" y2="19" />
            <line x1="5" y1="12" x2="19" y2="12" />
          </svg>
          تیکت جدید
        </Button>
      </div>
      {!canCreate && (
        <div className="rounded-btn border border-amber-200 bg-amber-50 px-4 py-3 text-small text-amber-800">
          به سقف تیکت‌های باز ({maxOpen.toLocaleString("fa-IR")}) رسیده‌اید. لطفاً منتظر پاسخ بمانید یا یکی از تیکت‌های باز را ببندید تا بتوانید تیکت جدید ثبت کنید.
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-5 items-start">
        {/* Ticket list */}
        <Card padding="md" className="lg:col-span-1">
          <h2 className="text-h2 text-text-main mb-4">تیکت‌های من</h2>
          {isLoading ? (
            <div className="space-y-3">
              <div className="h-20 bg-surface-hover rounded animate-pulse" />
              <div className="h-20 bg-surface-hover rounded animate-pulse" />
            </div>
          ) : (tickets || []).length === 0 ? (
            <div className="text-center py-10">
              <p className="text-small text-text-tertiary">هنوز تیکتی ثبت نکرده‌اید</p>
            </div>
          ) : (
            <div className="space-y-2">
              {(tickets as Record<string, unknown>[]).map((t) => {
                const sb = statusBadge[t.status as string] || statusBadge.open;
                return (
                  <button
                    key={String(t.ticket_id)}
                    onClick={() => handleSelect(Number(t.ticket_id))}
                    className={`w-full text-right p-3.5 rounded-btn border transition-all ${
                      selectedId === Number(t.ticket_id)
                        ? "border-primary bg-primary-light"
                        : "border-line hover:bg-surface-hover"
                    }`}
                  >
                    <div className="flex items-center justify-between gap-2">
                      <p className="text-small font-semibold text-text-main truncate">{t.subject as string}</p>
                      <div className="flex items-center gap-1.5 shrink-0">
                        <span className="font-mono text-tiny bg-surface-hover border border-line px-1.5 py-0.5 rounded text-text-tertiary">
                          #{String((t.ticket_number as string) ?? String(t.ticket_id).padStart(4,'0').slice(0,8))}
                        </span>
                        {(t.unread_count as number) > 0 && (
                          <span className="min-w-[18px] h-[18px] px-1 rounded-full bg-red-600 text-white text-[10px] font-bold flex items-center justify-center">
                            {t.unread_count as number}
                          </span>
                        )}
                        <Badge variant={sb.variant}>{sb.label}</Badge>
                      </div>
                    </div>
                    <p className="text-small text-text-tertiary mt-1.5 truncate">
                      {t.last_message as string || categoryLabel[t.category as string] || ""}
                    </p>
                    <div className="flex items-center justify-between mt-2">
                      <span className="text-tiny text-text-tertiary">{categoryLabel[t.category as string] || t.category as string}</span>
                      <span className="text-tiny text-text-tertiary">{timeAgo(t.updated_at as string)}</span>
                    </div>
                  </button>
                );
              })}
            </div>
          )}
        </Card>

        {/* Conversation */}
        <Card padding="md" className="lg:col-span-2">
          {selectedId === null || !detail ? (
            <div className="text-center py-20">
              <div className="w-14 h-14 rounded-full bg-primary-light flex items-center justify-center mx-auto">
                <svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" className="text-primary">
                  <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
                </svg>
              </div>
              <p className="text-body text-text-tertiary mt-4">یک تیکت را برای مشاهده گفتگو انتخاب کنید</p>
            </div>
          ) : (
            <div className="flex flex-col h-full min-h-[420px]">
              <div className="flex items-center justify-between mb-1">
                <div className="flex items-center gap-2">
                  <h2 className="text-h2 text-text-main">{detail.subject}</h2>
                  <span className="font-mono text-tiny bg-primary-light border border-primary/20 px-2 py-0.5 rounded text-primary">
                    #{String((detail.ticket_number as string) ?? String(detail.ticket_id).padStart(4,'0').slice(0,8))}
                  </span>
                </div>
                {(statusBadge[detail.status] || statusBadge.open) && (
                  <Badge variant={(statusBadge[detail.status] || statusBadge.open).variant}>
                    {(statusBadge[detail.status] || statusBadge.open).label}
                  </Badge>
                )}
              </div>
              <div className="flex items-center gap-2 mb-5">
                <span className="text-tiny text-text-tertiary">{categoryLabel[detail.category] || detail.category}</span>
                <span className="text-tiny text-text-tertiary">•</span>
                <span className="text-tiny text-text-tertiary">اولویت: {priorityLabel[detail.priority] || detail.priority}</span>
                <span className="text-tiny text-text-tertiary">•</span>
                <span className="text-tiny text-text-tertiary">{new Date(detail.created_at).toLocaleDateString("fa-IR")}</span>
              </div>

              <div className="flex-1 space-y-4 overflow-y-auto max-h-[380px] pr-1">
                {(detail.messages || []).map((m: { message_id: number; content: string; is_admin_reply: boolean; created_at: string; attachments?: Attachment[] }) => (
                  <div key={m.message_id} className={`flex ${m.is_admin_reply ? "justify-end" : "justify-start"}`}>
                    <div
                      className={`max-w-[80%] rounded-btn px-4 py-3 text-small leading-relaxed ${
                        m.is_admin_reply
                          ? "bg-surface-hover text-text-main"
                          : "bg-primary text-white"
                      }`}
                    >
                      <TicketMessageContent content={m.content} />
                      <AttachmentList
                        attachments={m.attachments}
                        ticketId={selectedId}
                        isUserMessage={!m.is_admin_reply}
                      />
                      <p className={`text-tiny mt-1.5 ${m.is_admin_reply ? "text-text-tertiary" : "text-white/70"}`}>
                        {m.is_admin_reply ? "پشتیبانی آشا" : "شما"} • {timeAgo(m.created_at)}
                      </p>
                    </div>
                  </div>
                ))}
              </div>

              {detail.status !== "closed" ? (
                <div className="mt-4 pt-4 border-t border-line space-y-3">
                  <textarea
                    value={replyText}
                    onChange={(e) => setReplyText(e.target.value)}
                    placeholder="پیام خود را بنویسید..."
                    className="w-full min-h-[90px] p-3 rounded-btn border border-line text-small text-text-secondary bg-surface focus:outline-none focus:ring-2 focus:ring-primary/30 transition-all resize-none"
                  />
                  <FilePicker files={replyFiles} onChange={setReplyFiles} error={replyFileError} />
                  <div className="flex items-center gap-2">
                    <Button onClick={handleReply} loading={reply.isPending} disabled={!replyText.trim()}>
                      ارسال پیام
                    </Button>
                    <Button variant="secondary" onClick={() => setConfirmClose(true)}>
                      بستن تیکت
                    </Button>
                  </div>
                </div>
              ) : (
                <div className="mt-4 pt-4 border-t border-line">
                  <p className="text-small text-text-tertiary text-center">این تیکت بسته شده است</p>
                </div>
              )}
            </div>
          )}
        </Card>
      </div>

      {/* Create ticket modal */}
      <GlassModal open={showCreate} onClose={() => setShowCreate(false)} title="تیکت جدید">
        <div className="space-y-4">
          {!canCreate && (
            <div className="rounded-btn border border-amber-200 bg-amber-50 px-3 py-2 text-small text-amber-800">
              سقف تیکت‌های باز شما ({openCount.toLocaleString("fa-IR")}/{maxOpen.toLocaleString("fa-IR")}) پر است — تا یکی را نبندید نمی‌توانید تیکت جدید بسازید.
            </div>
          )}
          <div>
            <label className="block text-small font-medium text-text-secondary mb-1.5">موضوع</label>
            <input
              type="text"
              value={form.subject}
              onChange={(e) => setForm((f) => ({ ...f, subject: e.target.value }))}
              className={inputClass}
              placeholder="موضوع تیکت"
            />
          </div>
          <div>
            <label className="block text-small font-medium text-text-secondary mb-1.5">دسته‌بندی</label>
            <select
              value={form.category}
              onChange={(e) => setForm((f) => ({ ...f, category: e.target.value }))}
              className={inputClass}
            >
              {Object.entries(categoryLabel).map(([k, v]) => (
                <option key={k} value={k}>{v}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-small font-medium text-text-secondary mb-1.5">اولویت</label>
            <select
              value={form.priority}
              onChange={(e) => setForm((f) => ({ ...f, priority: e.target.value }))}
              className={inputClass}
            >
              <option value="low">کم</option>
              <option value="medium">متوسط</option>
              <option value="high">زیاد</option>
            </select>
          </div>
          <div>
            <label className="block text-small font-medium text-text-secondary mb-1.5">متن پیام</label>
            <textarea
              value={form.message}
              onChange={(e) => setForm((f) => ({ ...f, message: e.target.value }))}
              className="w-full min-h-[110px] p-3 rounded-btn border border-line text-small text-text-secondary bg-surface focus:outline-none focus:ring-2 focus:ring-primary/30 transition-all resize-none"
              placeholder="شرح مشکل یا سوال خود را بنویسید..."
            />
          </div>
          <div>
            <FilePicker files={createFiles} onChange={setCreateFiles} error={createFileError} />
            <p className="text-tiny text-text-tertiary mt-1.5">
              حداکثر {faNumber(maxFiles)} فایل — فرمت‌های مجاز: PNG، JPEG، PDF — تا {faNumber(maxMb)} مگابایت
            </p>
          </div>
          <Button
            className="w-full !py-3"
            onClick={handleCreate}
            loading={create.isPending}
            disabled={!form.subject.trim() || !form.message.trim() || !canCreate}
          >
            ثبت تیکت
          </Button>
        </div>
      </GlassModal>

      {/* Close ticket confirmation modal */}
      <GlassModal open={confirmClose} onClose={() => setConfirmClose(false)} title="بستن تیکت">
        <div className="space-y-4">
          <p className="text-small text-text-secondary leading-relaxed">
            آیا از بستن این تیکت مطمئن هستید؟ پس از بستن، تیکت برای پاسخ‌دهی جدید بسته شده و دیگر نمی‌توانید برای آن پیام ارسال کنید.
          </p>
          <div className="flex items-center gap-2 justify-end">
            <Button variant="secondary" onClick={() => setConfirmClose(false)}>
              انصراف
            </Button>
            <Button onClick={() => close.mutate(undefined, { onSuccess: () => setConfirmClose(false) })} loading={close.isPending}>
              بستن تیکت
            </Button>
          </div>
        </div>
      </GlassModal>
    </div>
  );
}

function AttachmentList({
  attachments,
  ticketId,
  isUserMessage,
}: {
  attachments?: Attachment[];
  ticketId: number | null;
  isUserMessage: boolean;
}) {
  if (!attachments || attachments.length === 0) return null;
  const style = isUserMessage
    ? "bg-black/20 text-white border border-white/10 hover:bg-black/35"
    : "bg-surface-muted border border-line text-text-secondary hover:bg-surface-hover";
  const alertStyle = isUserMessage
    ? "bg-black/20 text-white/70 border border-white/10"
    : "bg-surface-muted border border-line text-text-tertiary";
  return (
    <div className="flex flex-wrap gap-2 mt-2">
      {attachments.map((a) => {
        const blocked = a.scan_status === "infected" || ticketId === null;
        const inner = (
          <>
            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M21.44 11.05l-9.19 9.19a6 6 0 0 1-8.49-8.49l9.19-9.19a4 4 0 0 1 5.66 5.66l-9.2 9.19a2 2 0 0 1-2.83-2.83l8.49-8.48" />
            </svg>
            <span className="max-w-[160px] truncate">{a.original_name}</span>
            <span className="opacity-60 shrink-0">{formatSize(a.size_bytes)}</span>
            {a.scan_status === "infected" && (
              <span className="text-[10px] px-1 rounded bg-red-500/20 text-red-500 shrink-0">آلوده</span>
            )}
            {a.scan_status === "pending" && (
              <span className="text-[10px] px-1 rounded bg-amber-500/20 text-amber-600 dark:text-amber-300 shrink-0">در بررسی</span>
            )}
          </>
        );
        if (blocked) {
          return (
            <span key={a.attachment_id} className={`inline-flex items-center gap-1.5 rounded-lg px-2.5 py-1 text-tiny ${alertStyle} opacity-80`}>
              {inner}
            </span>
          );
        }
        return (
          <a
            key={a.attachment_id}
            href={supportApi.getAttachmentDownloadUrl(ticketId as number, a.attachment_id)}
            target="_blank"
            rel="noopener noreferrer"
            className={`inline-flex items-center gap-1.5 rounded-lg px-2.5 py-1 text-tiny ${style} transition-colors no-underline`}
          >
            {inner}
          </a>
        );
      })}
    </div>
  );
}

function faNumber(value: number): string {
  return value.toLocaleString("fa-IR");
}